/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept, simpleDisplayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "./ReferenceEntry";
import UriEntry, { CreateHyperlink } from "./UriEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "./MarkdownEntry";
import AttachmentEntry, { DisplayFromFHIRAttachment } from "./AttachmentEntry";
import ArrayEntry from "./ArrayEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import { artifactRelationshipTypeValueSet, artifactRelationshipTypeValueSetLookup, convertValueSetToCodeableConceptSet } from "../dictionaries/THOandFHIRValueSets";

//to create a hyperlink to an absolute or relative URL in a Reference Datatype
/* REMOVED 2025-10-29
const ResourceReferenceHyperlink = ({ reference }) => {
  if (reference) {
    if (typeof reference.reference === "string" && reference.reference.trim()) {
      return <CreateHyperlink url={reference.reference} display={displayStringFromFHIRReference(reference)} openInNewTab={true} />;
    } else if (reference.identifier) {
      let fli;
      //check if FEvIR Linking Identifier is found
      if (reference.identifier.system?.toLowerCase() === "https://fevir.net/fli" || reference.identifier.type?.text === "FEvIR Linking Identifier") {
        fli = reference.identifier.value;
      }
      if (fli) {
        return <CreateHyperlink url={"/FLI/" + fli} display={displayStringFromFHIRReference(reference)} openInNewTab={true} />;
        //return <a href={"/FLI/" + fli} target="_blank" rel="noopener noreferrer">{fli}</a>;
      }
    }
    return <>{displayStringFromFHIRReference(reference)}</>;
  }
  return <></>;
}
  */

const DisplayFromFHIRRelatesTo = ({ relatesTo }) => {
  if (relatesTo) {
    let type = relatesTo.type || "";
    if (typeof type === 'object') {
      type = simpleDisplayStringFromFHIRCodeableConcept(type);
    }
    let classifierDisplay = "";
    let classifierList = [];
    if (relatesTo.classifier?.length > 0) {
      for (let classifierEntry in relatesTo.classifier) {
        let classifierString = displayStringFromFHIRCodeableConcept(relatesTo.classifier[classifierEntry]);
        if (classifierString.substring(0, 81) === "Quotation (coded as: quotation from http://hl7.org/fhir/provenance-entity-role); ") {
          classifierString = "Quotation (coded as: quotation from http://hl7.org/fhir/provenance-entity-role); \"" + classifierString.substring(81) + "\"";
        }
        classifierList.push(classifierString);
      }
    }
    if (relatesTo.extension?.length > 0) {
      for (let extension of relatesTo.extension) {
        if (extension.url === "http://hl7.org/fhir/StructureDefinition/relatesto-classifier" &&
          extension.valueCodeableConcept) {
          let classifierString = displayStringFromFHIRCodeableConcept(extension.valueCodeableConcept);
          if (classifierString.substring(0, 81) === "Quotation (coded as: quotation from http://hl7.org/fhir/provenance-entity-role); ") {
            classifierString = "Quotation (coded as: quotation from http://hl7.org/fhir/provenance-entity-role); \"" + classifierString.substring(81) + "\"";
          }
          classifierList.push(classifierString);
        }
      }
    }
    if (classifierList.length) {
      classifierDisplay = classifierList.join('; ') || "";
    }
    let label = relatesTo.label || "";
    let display = relatesTo.display || "";
    let citation = relatesTo.citation || "";
    let document = relatesTo.targetAttachment || "";
    let canonical = relatesTo.targetUri || relatesTo.targetCanonical || "";
    let resourceReference = "";
    if (relatesTo.targetReference) {
      resourceReference = <DisplayFromFHIRReference reference={relatesTo.targetReference} />;
    } else if (relatesTo.resourceReference) {
      resourceReference = <DisplayFromFHIRReference reference={relatesTo.resourceReference} />;
    }
    let targetMarkdown = relatesTo.targetMarkdown || relatesTo.citation || relatesTo.display || "";
    return <div style={{ marginLeft: "24px" }}>
      <p><b>Relationship Type: </b> {type}</p>
      {classifierDisplay && <p><b>Classifier(s): </b> {classifierDisplay}</p>}
      {label && <p><b>Label: </b> {label}</p>}
      {display && <p><b>Description: </b> {display}</p>}
      {citation && <span><b>Citation: </b> <DisplayFromFHIRMarkdown markdown={citation} /><br /></span>}
      {targetMarkdown && <span><b>Description/Citation: </b> <DisplayFromFHIRMarkdown markdown={targetMarkdown} /></span>}
      {canonical && <p><b>URL: </b> <CreateHyperlink url={canonical} display={canonical} openInNewTab={true} /></p>}
      {document && <DisplayFromFHIRAttachment attachment={document} />}
      {resourceReference && <p><b>Resource Reference: </b> {resourceReference}</p>}
    </div>
  } else {
    return <></>
  }
}

const RelatesToDatatypeSelector = ({ targetDatatypeState, setTargetDatatypeState, targetAllowedDatatypes }) => {
  return <div>
    <p style={{ marginBottom: "0px" }}><b>Datatype for the referenced item:</b></p>
    <FormControl>
      <FormLabel id="radiodatatypeselector-label"
        style={{ color: "#000000", paddingBottom: "6px" }}>Choose one way to define the referenced item. Use Reference for FHIR Resources on the FEvIR Platform. <i>(If you Update with a different datatype, you will lose previous reference data.)</i></FormLabel>
      <RadioGroup aria-label="radiodatatypeselector" name={"radiodatatypeselector-reference"}
        value={targetDatatypeState} onChange={(e) => { setTargetDatatypeState(e.target.value); }}>
        {targetAllowedDatatypes.map((allowedDatatype, allowedDatatypeIndex) => {
          return <FormControlLabel
            key={allowedDatatypeIndex}
            value={allowedDatatype}
            control={<Radio color="primary" style={{ paddingTop: "4px", paddingBottom: "4px" }} />}
            name="radio-button-control"
            color="default"
            inputprops={{ 'aria-label': allowedDatatype }}
            label={<span style={{ textAlign: 'center' }}><b>{allowedDatatype}</b></span>}
            style={{ paddingLeft: "16px" }}
          />
        })}
      </RadioGroup>
    </FormControl></div>
};

const updateResourceState = (elementName, relatesToState, setResourceState, targetDatatypeState) => {
  let newRelatedArtifact = {};
  if (relatesToState.extension?.length) { newRelatedArtifact.extension = relatesToState.extension; }
  if (relatesToState.type) { newRelatedArtifact.type = relatesToState.type; }
  if (targetDatatypeState === "Attachment" &&
    relatesToState.targetAttachment && Object.keys(relatesToState.targetAttachment).length) {
    newRelatedArtifact.targetAttachment = relatesToState.targetAttachment;
  }
  if (targetDatatypeState === "uri" && relatesToState.targetUri) {
    newRelatedArtifact.targetUri = relatesToState.targetUri;
  }
  if (targetDatatypeState === "canonical" && relatesToState.targetCanonical) {
    newRelatedArtifact.targetCanonical = relatesToState.targetCanonical;
  }
  if (targetDatatypeState === "markdown" && relatesToState.targetMarkdown) {
    newRelatedArtifact.targetMarkdown = relatesToState.targetMarkdown;
  }
  if (targetDatatypeState === "Reference" &&
    relatesToState.targetReference && Object.keys(relatesToState.targetReference).length) {
    newRelatedArtifact.targetReference = relatesToState.targetReference;
  }
  if (Object.keys(newRelatedArtifact).length === 0) {
    newRelatedArtifact = null;
  }
  setResourceState(prevState => { return { ...prevState, [elementName]: newRelatedArtifact } });
};

const RelatesToEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
  startCollapsed, relatesToClassifierValueSet = [] }) => {

  const [relatesToState, setRelatesToState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
  const [targetDatatypeState, setTargetDatatypeState] = useState('none');
  const [relatesToClassifierArrayState, setRelatesToClassifierArrayState] = useState({});

  const targetAllowedDatatypes = ['uri', 'Reference', 'canonical', 'markdown', 'Attachment'];

  useEffect(() => {
    let startingRelatesTo = {
      "extension": [], "type": "", "targetMarkdown": "", "targetCanonical": "", "targetReference": "",
      "targetUri": "", "targetAttachment": ""
    };
    let startingRelatesToDatatype = 'none';
    let startingRelatesToClassifiers = { "classifiers": [] };
    if (startingValue) {
      if (startingValue.extension) {
        startingRelatesTo.extension = startingValue.extension;
        for (const extension of startingValue.extension) {
          if (extension.url === "http://hl7.org/fhir/StructureDefinition/relatesto-classifier" &&
            extension.valueCodeableConcept) {
            startingRelatesToClassifiers.classifiers.push(extension.valueCodeableConcept);
          }
        }
      }
      if (startingValue.type) {
        if (typeof startingValue.type === "string") {
          startingRelatesTo.type = artifactRelationshipTypeValueSetLookup[startingValue.type];
        } else {
          startingRelatesTo.type = startingValue.type;
        }
      }
      if (startingValue.classifier) {
        let classifierArray;
        if (Array.isArray(startingValue.classifier)) {
          classifierArray = startingValue.classifier;
        } else {
          classifierArray = [startingValue.classifier];
        }
        for (const classifier of classifierArray) {
          startingRelatesTo.extension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/relatesto-classifier",
            "valueCodeableConcept": classifier
          });
          startingRelatesToClassifiers.classifiers.push(classifier);
        }
      }
      if (startingValue.label) {
        startingRelatesTo.extension.push({
          "url": "http://hl7.org/fhir/StructureDefinition/relatesto-classifier",
          "valueCodeableConcept": { "text": startingValue.label }
        });
        startingRelatesToClassifiers.classifiers.push({ "text": startingValue.label });
      }
      if (startingValue.display) {
        startingRelatesTo.targetMarkdown = startingValue.display;
        startingRelatesToDatatype = "markdown";
      }
      if (startingValue.citation) {
        startingRelatesTo.targetMarkdown = startingValue.citation;
        startingRelatesToDatatype = "markdown";
      }
      if (startingValue.targetMarkdown) {
        startingRelatesTo.targetMarkdown = startingValue.targetMarkdown;
        startingRelatesToDatatype = "markdown";
      }
      if (startingValue.document) {
        startingRelatesTo.targetAttachment = startingValue.document;
        startingRelatesToDatatype = 'Attachment';
      }
      if (startingValue.targetAttachment) {
        startingRelatesTo.targetAttachment = startingValue.targetAttachment;
        startingRelatesToDatatype = 'Attachment';
      }
      if (startingValue.targetUri) {
        startingRelatesTo.targetUri = startingValue.targetUri;
        startingRelatesToDatatype = 'uri';
      }
      if (startingValue.resource) {
        startingRelatesTo.targetCanonical = startingValue.resource;
        startingRelatesToDatatype = 'canonical';
      }
      if (startingValue.targetCanonical) {
        startingRelatesTo.targetCanonical = startingValue.targetCanonical;
        startingRelatesToDatatype = 'canonical';
      }
      if (startingValue.resourceReference) {
        startingRelatesTo.targetReference = startingValue.resourceReference;
        startingRelatesToDatatype = 'Reference';
      }
      if (startingValue.targetReference) {
        startingRelatesTo.targetReference = startingValue.targetReference;
        startingRelatesToDatatype = 'Reference';
      }
    }
    setRelatesToState(JSON.parse(JSON.stringify(startingRelatesTo)));
    setTargetDatatypeState(startingRelatesToDatatype);
    setRelatesToClassifierArrayState(JSON.parse(JSON.stringify(startingRelatesToClassifiers)));
  }, []);

  useEffect(() => {
    if (relatesToClassifierArrayState.classifiers?.length) {
      setRelatesToState(prevState => {
        let newExtension = prevState.extension.filter(extension => extension.url !== "http://hl7.org/fhir/StructureDefinition/relatesto-classifier");
        for (const classifier of relatesToClassifierArrayState.classifiers) {
          newExtension.push({
            "url": "http://hl7.org/fhir/StructureDefinition/relatesto-classifier",
            "valueCodeableConcept": classifier
          });
        }
        return { ...prevState, "extension": newExtension };
      });
    }
  }, [relatesToClassifierArrayState])

  useEffect((() => {
    if (Object.keys(relatesToState).length) {
      updateResourceState(elementName, relatesToState, setResourceState, targetDatatypeState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [relatesToState, targetDatatypeState]);

  if (startCollapsedState) {
    return <>
      <div>
        <b>{fieldLabel}: </b>
        {startingValue && <DisplayFromFHIRRelatesTo relatesTo={startingValue} />}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </div>
    </>
  } else if (Object.keys(relatesToState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <CodeableConceptEntry elementName='type' fieldLabel='Relationship Type'
          startingValue={relatesToState.type} setResourceState={setRelatesToState}
          codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet(artifactRelationshipTypeValueSet)} />
        <p style={{ marginBottom: "0px" }}><b>The Referenced Item:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <RelatesToDatatypeSelector targetDatatypeState={targetDatatypeState}
            setTargetDatatypeState={setTargetDatatypeState} targetAllowedDatatypes={targetAllowedDatatypes} />
          {targetDatatypeState === "Attachment" &&
            <AttachmentEntry elementName='targetAttachment' setResourceState={setRelatesToState}
              startCollapsed={relatesToState.targetAttachment}
              fieldLabel={'Attachment for ' + fieldLabel} startingValue={relatesToState.targetAttachment} />}
          {targetDatatypeState === "uri" &&
            <UriEntry elementName='targetUri' setResourceState={setRelatesToState}
              fieldLabel={'URI for ' + fieldLabel} startingValue={relatesToState.targetUri} />}
          {targetDatatypeState === "canonical" &&
            <UriEntry elementName='targetCanonical' setResourceState={setRelatesToState}
              fieldLabel={'Canonical URI for ' + fieldLabel} startingValue={relatesToState.targetCanonical} />}
          {targetDatatypeState === "Reference" &&
            <ReferenceEntry elementName='targetReference' setResourceState={setRelatesToState}
              startCollapsed={relatesToState.targetReference} enableCreation={true}
              fieldLabel={'Resource Reference for ' + fieldLabel}
              startingValue={relatesToState.targetReference}
              selectAnyProfile={true} />
          }
          {targetDatatypeState === "markdown" &&
            <MarkdownEntry elementName='targetMarkdown' setResourceState={setRelatesToState}
              fieldLabel={'Description for ' + fieldLabel} startingValue={relatesToState.targetMarkdown} />}
        </div>
        <p style={{ marginBottom: "0px" }}><b>Classifiers of the Referenced Item:</b></p>
        <ArrayEntry datatype='CodeableConcept' elementName='classifiers'
          fieldLabel='Target Classifier'
          startEmptyArrayClosed={true} startCollapsed={true}
          valueSet={relatesToClassifierValueSet}
          startingValue={relatesToClassifierArrayState.classifiers}
          setResourceState={setRelatesToClassifierArrayState} />
      </div>
    </>
  }
}

export default RelatesToEntry;
export { DisplayFromFHIRRelatesTo };